import { useEffect } from 'react';
import { useRouteError } from 'react-router-dom';
import { isChunkLoadError, reloadForNewBuild } from './chunkReload';

/**
 * Route-level error screen. A missing page file after a deploy reloads into
 * the new build; anything else gets a calm message with a way out instead of
 * React Router's developer screen.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const newBuild = isChunkLoadError(error);
  const reloading = newBuild && wasReloadStarted(error);

  useEffect(() => {
    if (!newBuild) console.error(error);
  }, [error, newBuild]);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-lg font-semibold">
        {newBuild ? 'A new version is available' : 'Something went wrong'}
      </p>
      <p className="max-w-md text-sm text-muted-foreground">
        {reloading
          ? 'Loading the latest version…'
          : newBuild
            ? 'The app was updated while this page was open. Reload to continue.'
            : 'An unexpected error occurred. Reloading the page usually fixes it.'}
      </p>
      {!reloading && (
        <button
          type="button"
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      )}
    </div>
  );
}

/** Starts the one-time reload for this error (once per error object). */
const started = new WeakSet<object>();
function wasReloadStarted(error: unknown): boolean {
  const key = typeof error === 'object' && error ? error : null;
  if (key && started.has(key)) return true;
  const ok = reloadForNewBuild();
  if (key && ok) started.add(key);
  return ok;
}
